import { TextBox } from 'devextreme-react';
import { Column, DataGrid } from 'devextreme-react/data-grid';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Card, Col, Collapse, Input, Label, Row } from 'reactstrap';
import Swal from 'sweetalert2';
import erorSound from '../../assets/audio/alert-error.wav';
import { useAuth } from '../../auth';
import Breadcrumb from '../../components/common/breadcrumb';
import useFormatMessage from '../../utils/useFormatMessage';
import { getPersonKontrolByGrupajNo, markChassisScanned } from './api';

const PeronKontrol = () => {
    const { t } = useFormatMessage();
    const [dataSource, setDataSource] = useState([]);
    const [scannedSase, setScannedSase] = useState(new Set());
    const [barcode, setBarcode] = useState("");
    const inputRef = useRef(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [lastScanTime, setLastScanTime] = useState(0);
    const SCAN_DELAY = 100; // Prevent duplicate scans within 100ms
    const [isOpen, setISOpen] = useState(false)
    const [groupagePlatform, setGroupagePlatform] = useState("");

    const { hasRole } = useAuth(7);

    const swalConfig = {
        customClass: {
            container: 'custom-swal-container',
            popup: 'custom-swal-popup',
            title: 'custom-swal-title',
            confirmButton: 'custom-swal-confirm-button',
            cancelButton: 'custom-swal-cancel-button'
        },
        buttonsStyling: false,
        confirmButtonText: 'Tamam',
        background: '#ffffff',
        showCloseButton: true,
        width: 'auto',
        padding: '2rem'
    };
    const playErrorAlert = () => {
        if (navigator.vibrate) {
            navigator.vibrate(500);
        }
        const errorSound = document.getElementById("errorSound");
        errorSound.play();
    };

    const showError = (message) => {
        Swal.fire({
            ...swalConfig,
            title: '<div class="text-red-600">Uyarı</div>',
            html: `<div class="text-gray-700">${message}</div>`,
            icon: 'warning',
            confirmButtonColor: '#dc3545'
        });
    };

    const showSuccess = (message) => {
        Swal.fire({
            ...swalConfig,
            title: '<div class="text-green-600">Başarılı</div>',
            html: `<div class="text-gray-700">${message}</div>`,
            icon: 'success',
            confirmButtonColor: '#28a745'
        });
    };
    const updateScannedChassis = (chassisNo) => {
        setDataSource(prev => prev.map(item => {
            if (item.Sase === chassisNo) {
                return { ...item, is_scanned: true, Durum: "Okundu" };
            }
            return item;
        }));

        setScannedSase(prev => {
            const updated = new Set(prev).add(chassisNo);
            if (updated.size === dataSource.length) {
                showSuccessWithTimer(
                    'Başarılı',
                    'Tüm şaseler başarıyla tamamlandı!',
                    `${updated.size}/${dataSource.length}`
                );
            }
            return updated;
        });
    };
    const handleBarcodeInput = async (scannedValue) => {
        const currentTime = Date.now();
        if (currentTime - lastScanTime < SCAN_DELAY) {
            return; // Prevent duplicate scans
        }
        setLastScanTime(currentTime);

        if (dataSource.length === 0) {
            // If no chassis data loaded, treat as grupaj number
            setBarcode(scannedValue);
            handleGrupajScan(scannedValue);
        } else {
            // If chassis data exists, treat as chassis number
            handleChassisScanned(scannedValue);
        }
    };

    const handleChassisScanned = async (chassisNo) => {

        if (hasRole("peron_kontrol_writee") === false) {
            toast.error("Bu işlemi yapmaya yetkiniz yoktur.");
            return
        }
        const matchingSase = dataSource.find(item => item.Sase === chassisNo);

        if (!matchingSase) {
            playErrorAlert();
            showErrorWithTimer(
                'Uyarı',
                `Şase ${chassisNo} belirtilen grupaja dahil değildir.`
            );
            return;
        }
        // Check if chassis is in SANAL platform
        if (
            !(matchingSase.platform_info?.toLowerCase().includes("dptr") || matchingSase.platform_info?.toLowerCase().includes("peron"))
        ) {
            playErrorAlert();
            showErrorWithTimer(
                'Uyarı',
                'Araçlar Peronda veya Transfer bölgesinde değildir!',
                `Aracın mevcut adresi: ${matchingSase.platform_info}`
            );
            return;
        }
        if (scannedSase.has(chassisNo)) {
            playErrorAlert();
            showErrorWithTimer(
                'Uyarı',
                `Şase ${chassisNo} daha önce okutulmuştur.`
            );
            return;
        }


        // Check if chassis platform matches groupage platform
        if (
            !(matchingSase.platform_info?.toLowerCase().includes("dptr") || matchingSase.platform_info?.toLowerCase().includes("peron"))
        ) {
            playErrorAlert();
            showErrorWithTimer(
                'Uyarı',
                'Araçlar Peronda veya Transfer bölgesinde değildir!',
                `Aracın mevcut adresi: ${matchingSase.platform_info}\nHedef Peron: ${groupagePlatform}`
            );
            return;
        }


        try {
            const markResponse = await markChassisScanned({
                chassis_no: chassisNo,
                groupage_no: barcode
            });

            if (markResponse.success) {
                updateScannedChassis(chassisNo);
            } else {
                throw new Error(markResponse.error || "Şase kaydetme işlemi başarısız");
            }
        } catch (error) {
            console.error("Şase kaydedilirken hata:", error);
            playErrorAlert();
            showErrorWithTimer(
                'Hata',
                'Şase kaydedilirken bir hata oluştu. Lütfen tekrar deneyiniz.'
            );
        }
    };
    const showSuccessWithTimer = (title, message, size) => {
        Swal.fire({
            title: `<div class="text-green-600">${title}</div>`,
            html: `<div class="text-gray-700">
                <p>${message}</p>
                ${size ? `<h4 class="font-weight-bold mt-2">${size}</h4>` : ''}
            </div>`,
            icon: 'success',
            timer: 3000,
            timerProgressBar: true,
            showConfirmButton: false,
            background: '#ffffff',
            customClass: {
                popup: 'custom-swal-popup',
                title: 'custom-swal-title',
            },
        });
    };
    const showErrorWithTimer = (title, message, details = '') => {
        Swal.fire({
            title: `<div class="text-red-600">⚠️ ${title}</div>`,
            html: `<div class="text-gray-700">
                <h5><b>${message}</b></h5>
                ${details ? `<div class="mt-2">${details}</div>` : ''}
            </div>`,
            //   icon: 'warning',
            timer: 4000,
            timerProgressBar: true,
            showConfirmButton: false,
            background: '#ffffff',
            customClass: {
                popup: 'custom-swal-popup',
                title: 'custom-swal-title',
            },
        });
    };
    const handleGrupajScan = async (grupajNo) => {
        try {
            const response = await getPersonKontrolByGrupajNo(grupajNo);
            if (response.success && response.details) {
                // Store the groupage platform
                setGroupagePlatform(response.groupage_platform);

                const transformedData = response.details.map((item, index) => ({
                    id: index + 1,
                    Durum: item.is_scanned ? "Okundu" : "",
                    Sase: item.chassis_no,
                    Model: `${item.brand} ${item.model}`,
                    Renk: item.color,
                    Peron: item.platform_info,
                    platform_info: item.platform_info, // Keep original platform info
                    is_scanned: item.is_scanned
                }));

                const initialScanned = new Set(
                    response.details
                        .filter(item => item.is_scanned)
                        .map(item => item.chassis_no)
                );
                setDataSource(transformedData);
                setScannedSase(initialScanned);

                if (initialScanned.size === response.details.length) {
                    showSuccessWithTimer(
                        'Tüm Şaseler Okundu',
                        'Tüm şaseler başarıyla tarandı!',
                        `${initialScanned.size}/${response.details.length}`
                    );
                }
            } else {
                let error_message = response.error ? response.error : "Hata";
                showErrorWithTimer('Uyarı', t(error_message));
            }
        } catch (error) {
            console.error("Grupaj No verisi alınırken hata:", error);
            setDataSource([]);
        }
    };

    const handleGlobalBarcodeScan = async (e) => {

        const key = e.key?.trim();
        let invalidSase = document.getElementById('sase_okut') ? document.getElementById('sase_okut').value : "";
        if (key === 'Enter') {
            if (dataSource.length === 0) {
                handleGrupajScan(barcode);
            } else {
                const matchingSase = dataSource.find(item => item.Sase === document.getElementById('sase_okut').value);
                if (matchingSase) {
                    if (hasRole("peron_kontrol_write") === false) {
                        toast.error("Bu işlemi yapmaya yetkiniz yoktur.");
                        return
                    }

                    // Check if chassis is in SANAL platform
                    if (!(matchingSase.platform_info?.toLowerCase().includes("dptr") || matchingSase.platform_info?.toLowerCase().includes("peron"))) {
                        playErrorAlert();
                        showErrorWithTimer(
                            'Uyarı',
                            'Araçlar Peronda veya Transfer bölgesinde değildir!',
                            `Aracın mevcut adresi: ${matchingSase.platform_info}`
                        );
                        if (document.getElementById('sase_okut')) document.getElementById('sase_okut').value = "";
                        return;
                    }
                    // Check if already scanned
                    if (scannedSase.has(invalidSase)) {
                        playErrorAlert();
                        showErrorWithTimer(
                            'Uyarı',
                            `Şase ${invalidSase} daha önce okutulmuştur.`
                        );
                        if (document.getElementById('sase_okut')) document.getElementById('sase_okut').value = "";
                        return;
                    }

                    try {
                        const markResponse = await markChassisScanned({
                            chassis_no: invalidSase,
                            groupage_no: barcode
                        });

                        if (markResponse.success) {
                            setDataSource(prev => {
                                const updatedDataSource = prev.map(item => {
                                    if (item.Sase === matchingSase.Sase) {
                                        item.is_scanned = true;
                                        item.Durum = "Okundu";
                                    }
                                    return item;
                                });
                                return updatedDataSource;
                            });

                            setScannedSase(prev => {
                                const updatedScannedSase = new Set(prev).add(invalidSase);
                                if (updatedScannedSase.size === dataSource.length) {
                                    setTimeout(() => {
                                        Swal.fire({
                                            title: t("Tüm Şaseler Tamamlandı"),
                                            text: t("Tüm şase numaraları başarıyla okutuldu!"),
                                            icon: 'success',
                                            confirmButtonText: t("Tamam")
                                        });
                                    }, 300);
                                }
                                return updatedScannedSase;
                            });

                            toast.success(` ✔️ Okutma işlemi başarılı  `);
                        } else {
                            playErrorAlert();
                            setTimeout(() => {
                                Swal.fire({
                                    title: "Hata",
                                    text: markResponse.error,
                                    icon: 'error',
                                    confirmButtonText: 'Tamam'
                                });
                            }, 300);
                        }
                    } catch (error) {
                        console.log(error.error);
                        playErrorAlert();
                        setTimeout(() => {
                            Swal.fire({
                                title: "Hata",
                                text: "Şase kaydetme işlemi başarısız",
                                icon: 'error',
                                confirmButtonText: 'Tamam'
                            });
                        }, 300);
                    }
                } else {
                    playErrorAlert();
                    setTimeout(() => {
                        Swal.fire({
                            html: `
                            <div style="text-align: center; color: #333;">
                                <div style="font-size: 2rem; color: #e74c3c;">⛔</div>
                                <h1 style="font-size: 1.5rem; font-weight: bold; margin: 0.5rem 0; color: #333;">
                                    Şase Uyarısı!
                                </h1>
                                <p style="font-size: 1rem;">
                                    <strong style="color: #e74c3c;">${invalidSase}</strong> kodlu şase mevcut grupaja dahil değil.
                                </p>
                            </div>
                        `,
                            confirmButtonText: 'Kapat',
                            confirmButtonColor: '#d9534f',
                            showCloseButton: false,
                            background: '#fff',
                            width: '280px',
                            allowOutsideClick: false,
                            padding: '1rem',
                            customClass: {
                                popup: 'rounded-lg border shadow-sm',
                                confirmButton: 'px-4 py-2 rounded bg-red-500 text-white hover:bg-red-600'
                            }
                        });
                    }, 300);
                }
            }
            e.preventDefault();
            if (document.getElementById('sase_okut')) document.getElementById('sase_okut').value = "";
        }
    };

    useEffect(() => {
        window.addEventListener('keydown', handleGlobalBarcodeScan);
        return () => {
            window.removeEventListener('keydown', handleGlobalBarcodeScan);
        };
    }, [dataSource, t]);

    const clearGrupaj = () => {
        setBarcode("");
        setDataSource([]);
        setScannedSase(new Set());
        // inputRef.current.focus(); //TODO
    };
    const handleKeyPress = (e) => {
        if (e.event.key === 'Enter') {
            handleSearch();
            e.event.preventDefault();
        }
    };
    const handleSearch = () => {
        if (barcode) {
            handleGrupajScan(barcode);
        }
    };

    const handleArrowClick = () => {
        const missingSase = dataSource.filter(item => !scannedSase.has(item.Sase));
        if (missingSase.length > 0) {
            setTimeout(() => {
                Swal.fire({
                    html: `<div style="line-height: 1.4; color: #555;">
                        Aşağıdaki şase numaraları eksik. Bu bilgiler yöneticinize iletilecek.<br/><br/>
                        <ul style="padding-left: 20px;">
                        ${missingSase.map(item => `<li style="color: #007bff;">${item.Sase}</li>`).join('')}
                        </ul><br/><strong>İşlemi onaylıyor musunuz?</strong>
                        </div>`,
                    showCancelButton: true,
                    confirmButtonText: t("Evet"),
                    cancelButtonText: t("Hayır"),
                    background: '#ffffff',
                    customClass: {
                        title: 'swal-title',
                        html: 'swal-html'
                    }
                }).then((result) => {
                    if (result.isConfirmed) {
                        Swal.fire({
                            title: 'Onaylandı',
                            text: 'İşlem tamamlanmıştır.',
                            icon: 'success',
                            confirmButtonText: 'Tamam',
                            customClass: {
                                title: 'swal-title',
                                html: 'swal-html'
                            }
                        });
                        setTimeout(() => {
                            setDataSource([]);
                            setBarcode("");
                        }, 300);
                    }
                });
            }, 300);
        } else {
            Swal.fire({
                title: t("Başarıyla Tamamlandı"),
                text: t("Tüm şase numaraları başarıyla okutulmuştur."),
                icon: 'success',
                confirmButtonText: t("Tamam")
            }).then(() => {
                setDataSource([]);
                setBarcode("");
            });
        }
    };
    const gridOptions = {
        columnHidingEnabled: true,
        columnMinWidth: isMobile ? 120 : 'auto',
        width: '100%',
        showBorders: true,
        showRowLines: true,
        showColumnLines: true,
        columnResizingMode: 'widget',
        allowColumnResizing: true,
        scrolling: {
            mode: isMobile ? 'standard' : 'virtual',
            showScrollbar: 'always'
        }
    };

    return (
        <>
            <Breadcrumb current={t("Peron Kontrol")} />
            <audio id="errorSound" src={erorSound} preload="auto"></audio>
            <Card className='p-2
            '>
                <Row className='m-2 space-y-4'>
                    <Col md="4">
                        <Label style={{ fontWeight: "600" }} className="block text-sm font-medium mb-2">{t("GrupajNo")}&emsp;</Label>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div className="input-group pill-input-group"
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "5fr 1fr"
                                }}
                            >
                                <TextBox
                                    showClearButton={true}
                                    value={barcode}
                                    onValueChanged={(e) => {
                                        if (!e.value) clearGrupaj();
                                        setBarcode(e.value);
                                    }}
                                    placeholder={t("Grupaj No Giriniz") + "..."}
                                    id='grupajNo'
                                />
                                <div className="input-group-append" >
                                    <Button
                                        color='info'
                                        onClick={() => handleGrupajScan(barcode)}
                                        style={{ padding: "8px 12px" }}>
                                        <i className='fa fa-search'></i>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>

                {dataSource.length > 0 && (
                    <>
                        <Row className='m-2'>
                            <Col md="4" className='flex justify-between items-center mb-2'>
                                <Label className='text-sm font-medium' style={{ fontWeight: "600", display: "flex", justifyContent: "space-between" }}>
                                    <span>{t("Şase")}</span>
                                    <span className={`text-sm font-medium ${scannedSase.size === dataSource.length
                                        ? 'text-green-600'
                                        : 'text-red-600'
                                        }`}
                                        style={{
                                            color: scannedSase.size === dataSource.length ? '#28a745' : '#dc3545'
                                        }}
                                    >
                                        {scannedSase.size} / {dataSource.length}
                                    </span>
                                </Label>
                                <Input
                                    disabled={!(hasRole("peron_kontrol_write"))}
                                    id='sase_okut'
                                    className="form-control input-sm text-box single-line w-full"
                                    placeholder={t("Şase Okutunuz") + "..."}
                                />
                                {/* <Button onClick={handleArrowClick} className="mt-2" color="warning" style={{ width: "max-content" }}>
                                    {t(`
                                        İşlemi Bitir
                                        `)}
                                </Button> */}
                            </Col>
                        </Row>

                        <Row className='mx-1 mx-md-2 mt-4'>
                            <Col xs="12" md="12" lg="8">
                                <p
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "end",
                                        fontWeight: "600"
                                    }}
                                >
                                    <span>
                                        {groupagePlatform}
                                    </span>
                                    <span onClick={() => setISOpen(!isOpen)} style={{
                                        cursor: "pointer",
                                        paddingInline: "0.5rem",
                                    }}>
                                        {t("DETAY")} <i className={`fa fa-chevron-${isOpen ? "up" : "down"}
                                         cursor-pointer`} ></i>
                                    </span>

                                </p>
                                <Collapse isOpen={!isOpen}>
                                    <DataGrid DataGrid
                                        width={"auto"}
                                        {...gridOptions}
                                        // className='datagrid-color'
                                        showBorders={true}
                                        showRowLines={true}
                                        showColumnLines={true}

                                        dataSource={dataSource}
                                        onRowPrepared={(e) => {
                                            if (e.rowType === 'data') {

                                                if (e.data.is_scanned === true) { // Be more explicit with the condition
                                                    e.rowElement.style.backgroundColor = '#f0f0f0';
                                                    // Add a class for better debugging
                                                    e.rowElement.classList.add('scanned-row');
                                                }
                                            }
                                        }}

                                    >
                                        <Column
                                            dataField="Sase"
                                            caption={t("Şase")}
                                            width={isMobile ? 140 : undefined}
                                        />
                                        <Column
                                            dataField="Model"
                                            caption={t("Model")}
                                            width={isMobile ? 140 : undefined}
                                        />
                                        {/* <Column
                                        dataField="Renk"
                                        caption={t("Renk")}
                                        width={isMobile ? 120 : undefined}
                                    />
                                    <Column
                                        dataField="Peron"
                                        caption={t("Peron")}
                                        width={isMobile ? 120 : undefined}
                                    /> */}
                                    </DataGrid>
                                </Collapse>
                                <Collapse
                                    isOpen={isOpen}
                                    className="overflow-hidden"
                                >
                                    {dataSource.length > 0 && (
                                        <div className="space-y-1">
                                            {dataSource.map((item) => (
                                                <div
                                                    key={item.id}
                                                    style={{
                                                        backgroundColor: item.is_scanned ? "#f0f0f0" : "white"
                                                    }}
                                                    className={`p-2 rounded-md border  }`}
                                                >
                                                    <div className="grid grid-cols-3 text-sm gap-x-2">
                                                        <div className="col-span-3 font-medium truncate">
                                                            <b>{item.Sase}</b>
                                                        </div>
                                                        <div className="truncate">{item.Model} - {item.Renk}</div>
                                                        <div className="truncate">{item.Peron}</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </Collapse>

                            </Col>

                        </Row>
                    </>
                )}
            </Card >
        </>
    );
};

export default PeronKontrol;






